// SEO-critical FAQs for city and locality pages.
// Answers must be truthful, INR-based, India-specific.
// These render as visible <details> AND get emitted as JSONLD FAQPage schema,
// so Google can show them in "People Also Ask" boxes.

export const CITY_FAQS = (cityName) => ([
  {
    q: `What is the average monthly rent for a 2BHK in ${cityName}?`,
    a: `Rents in ${cityName} vary widely by locality and furnishing. As a broad range, semi-furnished 2BHK apartments typically range from ₹18,000 to ₹55,000 per month across most localities, with premium neighbourhoods commanding higher rates. To-Let Rentals' AI Rent Estimator gives a hyper-local estimate for any address in seconds.`,
  },
  {
    q: `Is To-Let Rentals really zero brokerage in ${cityName}?`,
    a: `Yes. We charge zero brokerage on every rental and sale listed on our platform. Owners list free, renters contact them free. We never take a cut of your rent, security deposit, or purchase price. Our only paid feature is optional "Boost" for owners who want higher visibility.`,
  },
  {
    q: `How do I verify a landlord in ${cityName} before signing?`,
    a: `Ask the landlord for these three documents before paying any deposit: (1) latest property tax receipt in their name, (2) Aadhaar or PAN card, (3) prior sale deed or ownership document. Cross-check the address on the tax receipt with the property. On To-Let Rentals, owners with a green "Verified" badge have submitted government ID and been reviewed by our team.`,
  },
  {
    q: `What is the typical security deposit in ${cityName}?`,
    a: `Deposits vary by city and neighbourhood. In ${cityName}, deposits typically range between 2 and 10 months of rent depending on the locality's norms. Always insist on a written, signed rent agreement mentioning the exact deposit amount and refund timeline before paying.`,
  },
  {
    q: `Do I need to register my rent agreement in ${cityName}?`,
    a: `In India, rent agreements for periods longer than 11 months should legally be registered at the sub-registrar's office. For 11-month agreements, notarisation on a ₹100 stamp paper is common. Registration protects you in court if a dispute arises — we strongly recommend it regardless of duration.`,
  },
  {
    q: `Can I rent a flat in ${cityName} without brokerage?`,
    a: `Absolutely. To-Let Rentals lists thousands of homes directly from owners in ${cityName} with zero brokerage — no middlemen, no hidden fees. Sign up free, browse verified owners, and contact them directly on WhatsApp or by phone.`,
  },
  {
    q: `What's the best area to rent in ${cityName} for working professionals?`,
    a: `The best area depends on your workplace and budget. Consider commute time on Google Maps during rush hour, metro connectivity, and safety of the neighbourhood. Read our locality guides on To-Let Rentals for honest area-by-area breakdowns with rent ranges, pros and cons.`,
  },
  {
    q: `Are pets allowed in most rentals in ${cityName}?`,
    a: `Around 20-30% of owners in ${cityName} are pet-friendly, though this varies by society. Filter for pet-friendly listings on To-Let Rentals and always confirm in writing before signing the agreement.`,
  },
]);

export const LOCALITY_FAQS = (locality, city) => ([
  {
    q: `How much is the average rent in ${locality}, ${city}?`,
    a: `Rents in ${locality} vary by BHK and furnishing. Use our AI Rent Estimator (free on the homepage) for a hyper-local monthly rent estimate based on real listings and market data. As a rough guide, 1BHKs typically start from ₹12,000 and 3BHKs from ₹35,000 in most Indian urban localities.`,
  },
  {
    q: `Is ${locality} a good area to live in?`,
    a: `${locality} attracts residents for its blend of connectivity, amenities and local vibe. Before you commit, visit the area in the evening to gauge traffic and noise, check nearby grocery stores and schools, and speak to 2-3 existing residents. Our locality guides on the blog give an honest area rating with pros and cons.`,
  },
  {
    q: `Are there zero-brokerage flats in ${locality}?`,
    a: `Yes. To-Let Rentals lists rental homes in ${locality} directly from owners with zero brokerage. Sign up free and contact the owner directly — no broker calls, no hidden fees.`,
  },
  {
    q: `What amenities are near ${locality}?`,
    a: `Most ${locality} properties are within 1-3 km of grocery stores, schools, hospitals and public transport. Check each listing on To-Let Rentals for its specific distance to metro/bus stops and nearby landmarks.`,
  },
  {
    q: `Is ${locality} safe for women and families?`,
    a: `${locality} is generally considered safe by residents, but safety depends on the specific society and street. Look for gated societies with 24/7 security, decent street lighting, and confirm the area with existing tenants via society WhatsApp groups. Never sign an agreement based on daytime visits alone — go once at night too.`,
  },
]);
